import Phaser from 'phaser';
import { audio } from '../audio';
import { buildMechArt } from '../art/mech';
import { buildFxArt, buildIconArt, buildWorldArt } from '../art/world';
import { COLORS } from '../config/palette';
import { GAME_HEIGHT, GAME_WIDTH, SCENES } from '../config/constants';
import { getRun, startRun } from '../core/run';
import { save } from '../core/save';
import type { MechId } from '../core/types';
import { MECH_IDS } from '../core/types';
import { buildBossArt } from '../systems/bosses/art';
import { buildEnemyArt } from '../systems/enemies/art';

/** public/assets/manifest.json — generated art; any key listed here overrides the procedural fallback. */
interface AssetManifest {
  version: number;
  images?: Record<string, string>;
}

export class BootScene extends Phaser.Scene {
  constructor() {
    super(SCENES.BOOT);
  }

  preload(): void {
    const w = 560;
    const x = GAME_WIDTH / 2 - w / 2;
    const y = GAME_HEIGHT / 2 + 40;
    this.add.rectangle(GAME_WIDTH / 2, y, w + 4, 8).setStrokeStyle(1, COLORS.uiBorder);
    const bar = this.add.rectangle(x, y, 1, 4, COLORS.volt).setOrigin(0, 0.5);
    this.load.on(Phaser.Loader.Events.PROGRESS, (p: number) => (bar.width = Math.max(1, w * p)));

    this.load.json('asset-manifest', 'assets/manifest.json');
    this.load.on('filecomplete-json-asset-manifest', (_key: string, _type: string, data: AssetManifest) => {
      for (const [key, path] of Object.entries(data?.images ?? {})) {
        if (!this.textures.exists(key)) this.load.image(key, path);
      }
    });
    this.load.on(Phaser.Loader.Events.FILE_LOAD_ERROR, (file: Phaser.Loader.File) => {
      console.warn(`[boot] optional asset unavailable: ${file.key}`);
    });
  }

  create(): void {
    void this.finishBoot();
  }

  private async finishBoot(): Promise<void> {
    await this.waitForFonts();

    // Procedural builders skip any key that was already loaded from the manifest.
    const builders: [string, (scene: Phaser.Scene) => void][] = [
      ['fx', buildFxArt],
      ['world', buildWorldArt],
      ['icons', buildIconArt],
      ['mech', buildMechArt],
      ['enemies', buildEnemyArt],
      ['bosses', buildBossArt],
    ];
    for (const [name, build] of builders) {
      try {
        build(this);
      } catch (err) {
        console.error(`[boot] art builder "${name}" failed`, err);
      }
    }

    document.getElementById('boot-msg')?.remove();
    audio.applySettings(save.settings);

    const params = new URLSearchParams(window.location.search);
    const target = params.get('scene');
    if (target && this.scene.get(target)) {
      this.scene.start(target);
      return;
    }
    if (params.has('autostart')) {
      const requested = (params.get('autostart') || 'volt') as MechId;
      const mechId = MECH_IDS.includes(requested) ? requested : 'volt';
      const seedParam = params.get('seed');
      startRun({ mechId, seed: seedParam ? Number(seedParam) : undefined });
      this.scene.start(SCENES.GAME, { nodeId: getRun()!.nodeId });
      return;
    }
    this.scene.start(SCENES.MENU);
  }

  private async waitForFonts(): Promise<void> {
    const timeout = new Promise<void>((resolve) => setTimeout(resolve, 2500));
    const fonts = Promise.all(
      ['600 32px "Chakra Petch"', '700 32px "Chakra Petch"', '500 24px "Rajdhani"', '700 24px "Rajdhani"', '400 16px "JetBrains Mono"'].map(
        (f) => document.fonts.load(f).catch(() => undefined),
      ),
    ).then(() => undefined);
    await Promise.race([fonts, timeout]);
  }
}
